import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useCallback, useEffect, useState } from 'react';
import type { JackpotPool, JackpotProduct, JackpotWonMessage } from '../@types';
import { PRODUCT } from '../types/enums';
import useWebsocket from './useWebsocket';

const useWinningJackpot = (product: JackpotProduct, enabled = true) => {
  const { userId } = useAuth();
  const sessionId = enabled ? userId : null;
  const [winnerSession, setWinnerSession] = useState({
    sessionId,
    winningJackpot: null as JackpotPool | null,
  });

  // Reset during render so a previous session's prize can never reach the UI.
  if (winnerSession.sessionId !== sessionId) {
    setWinnerSession({ sessionId, winningJackpot: null });
  }

  const jackpotUpdater = useCallback(
    (data: JackpotWonMessage[]) => {
      if (!enabled || !userId) return;

      data.forEach((message) => {
        const messageData = message[userId];

        if (!messageData || messageData.event !== 'jackpotWinner') {
          return;
        }

        // The feed API's legacy sportsbook websocket event predates the product
        // field. Casino events must identify themselves explicitly, so a missing
        // product can safely be treated as sportsbook during the migration.
        const messageProduct = messageData.payload.product ?? PRODUCT.SPORTSBOOK;

        if (messageProduct !== product) {
          return;
        }

        setWinnerSession({
          sessionId: userId,
          winningJackpot: { ...messageData.payload, product: messageProduct },
        });
      });
    },
    [enabled, product, userId]
  );

  const { joinRoom: joinJackpotRoom } = useWebsocket({
    callback: jackpotUpdater,
  });

  const clearJackpot = useCallback(() => {
    setWinnerSession((current) => ({ ...current, winningJackpot: null }));
  }, []);

  useEffect(() => {
    if (enabled && userId) {
      joinJackpotRoom(userId);
    }
  }, [enabled, joinJackpotRoom, userId]);

  return {
    clearJackpot,
    winningJackpot: winnerSession.winningJackpot,
  };
};

export default useWinningJackpot;
